import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FileText, ChevronDown, Check } from 'lucide-react'
import type { NegotiationDocumentInfo } from '../../services/negotiationsApi'

const ROLE_ICONS: Record<string, string> = {
  PRIMARY: '',
  ANNEX: 'A',
  SIDE_LETTER: 'S',
  SCHEDULE: 'Sch',
  DISCLOSURE: 'D',
  OTHER: '',
}

interface DocumentDropdownProps {
  documents: NegotiationDocumentInfo[]
  activeDocumentId: number | null
  onSelect: (doc: NegotiationDocumentInfo) => void
}

/**
 * Compact document dropdown for multi-document negotiations.
 *
 * Only renders when there are 2+ documents (single-doc negotiations
 * don't need this). Fits within 320px Word Add-in task pane.
 */
export function DocumentDropdown({ documents, activeDocumentId, onSelect }: DocumentDropdownProps) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)

  // Don't render for single-document negotiations
  if (!documents || documents.length <= 1) return null

  const sorted = [...documents].sort((a, b) => a.sort_order - b.sort_order)
  const active = sorted.find(d => d.id === activeDocumentId) ?? sorted[0]

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 w-full px-2.5 py-1.5 text-[11px] bg-neutral-50 dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-750 transition-colors"
        title={t('negotiate.switchDocument', 'Switch document')}
      >
        <FileText className="w-3.5 h-3.5 shrink-0 text-primary-500" />
        <span className="flex-1 text-left truncate font-medium text-neutral-700 dark:text-neutral-300">
          {active?.label || active?.document_title || t('negotiate.primaryDocument', 'Main document')}
        </span>
        <span className="text-[10px] text-neutral-400 shrink-0">
          v{active?.current_version ?? 0}
        </span>
        <ChevronDown className={`w-3 h-3 shrink-0 text-neutral-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute left-0 right-0 top-full mt-0.5 z-50 bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-lg shadow-lg py-0.5 max-h-48 overflow-y-auto">
            {sorted.map(doc => {
              const isActive = doc.id === active?.id
              const roleTag = ROLE_ICONS[doc.role]
              return (
                <button
                  key={doc.id}
                  onClick={() => { onSelect(doc); setOpen(false) }}
                  className={`flex items-center gap-1.5 w-full px-2.5 py-1.5 text-[11px] transition-colors ${
                    isActive
                      ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                      : 'hover:bg-neutral-50 dark:hover:bg-neutral-750 text-neutral-700 dark:text-neutral-300'
                  }`}
                >
                  {roleTag ? (
                    <span className="w-4 h-4 shrink-0 flex items-center justify-center rounded bg-neutral-200 dark:bg-neutral-700 text-[9px] font-bold text-neutral-500 dark:text-neutral-400">
                      {roleTag}
                    </span>
                  ) : (
                    <FileText className="w-3.5 h-3.5 shrink-0 text-neutral-400" />
                  )}
                  <span className="flex-1 text-left truncate">
                    {doc.label || doc.document_title}
                  </span>
                  <span className="text-[10px] text-neutral-400 shrink-0">v{doc.current_version}</span>
                  {isActive && <Check className="w-3 h-3 shrink-0 text-primary-500" />}
                </button>
              )
            })}
          </div>
        </>
      )}
    </div>
  )
}
